import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { MatTable } from '@angular/material/table';
import { TooltipPosition } from '@angular/material/tooltip';

import { IHasIdAndName, IHasIdAndNameAndWorkflowId } from '../../interfaces/has-id-and-name.interface';
import { MagicStructureMembership, MagicStructureMembershipWithFormGroup } from '../../models/magic-structures/magic-structure-membership.model';
import { MagicStructureMember } from '../../models/magic-structures/magic-structure-member.model';
import { MagicStructureDefinition } from '../../models/magic-structures/magic-structure-def.model';
import { Folder } from '../../models/site-content/folder.model';

// Define an interface for interacting with this component.
export interface IManageMagicStructureMemberships {
    clearMemberships(): void;
    getMemberships(): MagicStructureMembershipWithFormGroup[];
    setMemberships(memberships: MagicStructureMembershipWithFormGroup[]): void;

    addNewMembership(membership: MagicStructureMembershipWithFormGroup, renderRowsFlag: boolean): MagicStructureMembershipWithFormGroup[];
    addNewMemberToSelectedMembership(member: MagicStructureMember, renderRowsFlag: boolean): MagicStructureMembershipWithFormGroup[];

    renderRows(): void;
}

@Component({
    selector: 'app-magic-structure-membership',
    templateUrl: './magic-struct-membership.component.html',
    styleUrls: ['./magic-struct-membership.component.scss'],
    standalone: false
})
export class MagicStructureMembershipComponent implements OnInit {
    // Properties.
    // Input properties.
    @Input() folderOrFormInstance: IHasIdAndNameAndWorkflowId;

    // Output properties.
    @Output() onInit: EventEmitter<IManageMagicStructureMemberships> = new EventEmitter();
    @Output() onMembershipChange: EventEmitter<MagicStructureMembershipWithFormGroup[]> = new EventEmitter();
    @Output() onMembershipSelectionChanged: EventEmitter<MagicStructureMembershipWithFormGroup> = new EventEmitter();

    // Read only properties.
    private readonly colNames: string[] = ['MembershipName', 'RoleNames', 'Member', 'Action'];

    // View children proeprties.
    @ViewChild(MatTable) table: MatTable<MagicStructureMember>;
    @ViewChild('importMembershipsFile') importMembershipsFile;

    // Other properties.
    private magicStructureDef: MagicStructureDefinition = null; // Get allocated in ngOnInit().
    private structureMemberships: MagicStructureMembershipWithFormGroup[] = [];
    private addMemberButtonCanEnable: boolean = false;

    // Constructor.
    public constructor() { }

    // Life cycle methods.
    public ngOnInit(): void {
        this.onInit.emit(this);
    }

    // Methods called by my HTML code.
    public get TooltipPosition(): TooltipPosition {
        let position: TooltipPosition = 'below';

        return (position);
    }
    public get TooltipPositionRight(): TooltipPosition {
        let position: TooltipPosition = 'right';

        return (position);
    }

    public get MembershipTableColumnNames(): string[] {
        return (this.colNames);
    }

    public get StructureMemberships(): MagicStructureMembership[] {
        return (this.structureMemberships);
    }

    public MemberCanMoveUp(structureMembership: MagicStructureMembership, member: MagicStructureMember, memberIndex: number): boolean {
        let bCanMoveUp: boolean =
            ((structureMembership.members.length > 1) && (memberIndex > 0));

        return (bCanMoveUp);
    }
    public MemberCanMoveDown(structureMembership: MagicStructureMembership, member: MagicStructureMember, memberIndex: number): boolean {
        let bCanMoveDown: boolean =
            ((structureMembership.members.length > 1) && (memberIndex < structureMembership.members.length - 1));

        return (bCanMoveDown);
    }

    /*
    public get MembershipNameHeaderText(): string {
        let name: string = `${this.structureTypeName} Copy Name`;

        return name;
    }
    */

    public get MembershipNameHeaderText(): string {
        let name: string = `${this.structureTypeName} Copy Name`;

        return name;
    }

    public get MembershipSelectionCheckboxTooltip(): string {
        let tooltip: string = 'Select a membership checkbox in order to add additional members';

        return (tooltip);
    }

    public MembershipShowHideDetailTooltip(membership: MagicStructureMembership): string {
        let tooltip: string =
            (membership.membershipDetailsExpanded ?
                'Click here to hide membership detail' : 'Click here to show membership detail');

        return (tooltip);
    }

    public get MembershipTrashCanTooltip(): string {
        let tooltip: string = 'Click here to delete this membership';

        return (tooltip);
    }

    public MembershipDetailTdClass(membership: MagicStructureMembership): string {
        let tdClass: string =
            (this.DetailsExpanded(membership) ? 'expanded-detail-td' : 'hidden-detail-td');

        return (tdClass);
    }

    public MemberDetailRowClass(membership: MagicStructureMembership): string {
        let rowClass: string =
            (this.DetailsExpanded(membership) ? 'expanded-member-detail-row' : 'hidden-member-detail-row');

        return (rowClass);
    }

    public MemberDetailText(member: MagicStructureMember): string {
        return (member.displayName);
    }
    public MemberTypeText(member: MagicStructureMember): string {
        return (member.memberType);
    }

    private DetailsExpanded(membership: MagicStructureMembership): boolean {
        // Note:  putting this code in a method in case the following,
        //        referenced property, 'membershipDetailsExpanded', is
        //        not actually needed(might be able to use property
        //        'membershipSelected').
        return (membership.membershipDetailsExpanded);
    }

    public get MoveMemberUpTooltip(): string {
        let tooltip: string = 'Click here to move this member up';

        return (tooltip);
    }
    public get MoveMemberDownTooltip(): string {
        let tooltip: string = 'Click here to move this member down';

        return (tooltip);
    }
    public get DeleteMemberTooltip(): string {
        let tooltip: string = 'Click here to delete this member';

        return (tooltip);
    }

    public get AtLeastOneMembershipRequiredText(): string {
        let message: string = (this.structureMemberships.length == 0 ? 'At least one membership must be configured' : '');

        return (message);
    }
    
    public get CreateOneStructurePerMemberTooltip(): string {
        let structureType: string = this.structureTypeName.toLowerCase();
        let tooltip: string = `If checked, creates one ${structureType} copy per member`;

        return tooltip;
    }
    public get NumCopiesConfiguredLabel(): string {
        let label: string = `Number of ${this.structureTypeName.toLowerCase()} copies configured:`;

        return label;
    }
    public get NumCopiesConfiguredValue(): number {
        let numMemberships: number = (this.structureMemberships != null ? this.structureMemberships.length : 0);
        let numCopies: number = numMemberships;

        if (this.magicStructureDef.createOneStructurePerMember && (numCopies > 0)) {
            numCopies = 0;

            for (let iMembership: number = 0; iMembership < this.structureMemberships.length; iMembership++) {
                let membership: MagicStructureMembershipWithFormGroup = this.structureMemberships[iMembership];
                let numMembers: number = (membership.members != null ? membership.members.length : 0);

                numCopies += numMembers;
            }
        }

        return numCopies;
    }

    public get DisableMemberSectionAdvancement(): boolean {
        return (this.structureMemberships.length == 0);
    }

    public get ShowImportFormatButtonDisabled(): boolean {
        let disabled: boolean = false;

        /*
        if ((this.structureMemberships == null) || (this.structureMemberships.length == 0))
            disabled = true;
        else {
            for (let iMembership: number = 0; iMembership < this.structureMemberships.length; iMembership++) {
                let membership: MagicStructureMembershipWithFormGroup = this.structureMemberships[iMembership];

                if ((membership.name == null) || (membership.name.trim() == ''))
                    disabled = true;
                else if ((membership.roleNames == null) || (membership.roleNames.length == 0))
                    disabled = true;

                if (disabled)
                    break;
            }
        }
        */

        return disabled;
    }

    public get ShowImportMembershipsButtonDisabled(): boolean {
        let disabled: boolean = false;

        return disabled;
    }

    /*
    public getMemberType(membership: MagicStructureMembership): string {
        let memberType: string = null;

        if (membership.members.length == 0) {
            memberType = 'None';
        } else if (membership.members.length == 1) {
            memberType = membership.members[0].memberType;
        } else {
            memberType = membership.members[0].memberType;

            for (let iMember: number = 1; iMember < membership.members.length; iMember++) {
                if (membership.members[iMember].memberType != memberType) {
                    memberType = 'Multiple';

                    break;
                }
            }
        }

        return (memberType);
    }
    */

    public getMemberDisplayName(membership: MagicStructureMembership): string {
        let member: string = null;

        if (membership.members.length == 0) {
            member = 'None';
        } else if (membership.members.length == 1) {
            let memberType: string = membership.members[0].memberType;
            member = `1 ${memberType}`;
        } else {
            let memberType: string = membership.members[0].memberType;
            member = `${membership.members.length} ${memberType}s`;

            for (let iMember: number = 1; iMember < membership.members.length; iMember++) {
                if (membership.members[iMember].memberType != memberType) {
                    member = `${membership.members.length} members`;

                    break;
                }
            }
        }

        return (member);
    }

    // Handle control actions.
    public membershipSelectionChanged(structureMembership: MagicStructureMembershipWithFormGroup): void {
        /*
        if (structureMembership.membershipSelected) {
            // Deselect any other selected membership.
            this.deselectMembershipsExcept(structureMembership);
        }

        this.setAddButtonBasedOnMemberships();

        return;
        */
        this.onMembershipSelectionChanged.emit(structureMembership);
    }

    public arrowsExpandClicked(membership: MagicStructureMembership, eventData: any): void {
        // Toggle whether the specified membership's details are displayed.
        membership.membershipDetailsExpanded = (!membership.membershipDetailsExpanded);

        return;
    }

    // Implement IManageMagicStructureMemberships methods.
    public clearMemberships(): void {
        this.structureMemberships = [];
    }

    public getMemberships(): MagicStructureMembershipWithFormGroup[] {
        return this.structureMemberships;
    }

    public setMemberships(memberships: MagicStructureMembershipWithFormGroup[]): void {
        // NOTE:  THIS METHOD IS NOT YET IMPLEMENTED.
        return;
    }

    public addNewMembership(structureMembership: MagicStructureMembershipWithFormGroup, renderRowsFlag: boolean): MagicStructureMembershipWithFormGroup[] {
        let existingMembership: MagicStructureMembership =
            this.structureMemberships.find(m => m.name == structureMembership.name);

        if (existingMembership == undefined) {
            this.structureMemberships.push(structureMembership);
            if (renderRowsFlag)
                this.table.renderRows();
        }

        return this.structureMemberships;
    }

    public addNewMemberToSelectedMembership(structureMember: MagicStructureMember, renderRowsFlag: boolean): MagicStructureMembershipWithFormGroup[] {
        let structureMembership: MagicStructureMembership =
            this.structureMemberships.find(m => m.membershipSelected == true);

        if (structureMembership != null) {
            let existingMember: MagicStructureMember =
                structureMembership.members.find(sm => (sm.memberType == structureMember.memberType) && (sm.member == structureMember.member));

            if (existingMember == undefined) {
                structureMembership.members.push(structureMember);
                if (renderRowsFlag)
                    this.table.renderRows();

                this.setClearMemberInput(structureMember);
            }
        }

        return this.structureMemberships;
    }

    public renderRows(): void {
        this.table.renderRows();
    }

    // Note:  not sure if the following method is needed anymore.
    private setClearMemberInput(memberOutput: any): void {
        memberOutput['clearMemberInput'] = true;

        return;
    }

    public moveMemberUp(structureMembership: MagicStructureMembership, member: MagicStructureMember, memberIndex: number): void {
        if (memberIndex > 0) {
            let member: MagicStructureMember = structureMembership.members[memberIndex];

            // Remove the member and re-add it.
            structureMembership.members =
                structureMembership.members.filter(sm => (sm.memberType != member.memberType) || (sm.member != member.member));

            structureMembership.members.splice(memberIndex - 1, 0, member);

            this.table.renderRows();

            // Notify my parent of the change.
            this.onMembershipChange.emit(this.structureMemberships);
        }

        return;
    }
    public moveMemberDown(structureMembership: MagicStructureMembership, member: MagicStructureMember, memberIndex: number): void {
        if (memberIndex < structureMembership.members.length - 1) {
            let member: MagicStructureMember = structureMembership.members[memberIndex];

            // Remove the member and re-add it.
            structureMembership.members =
                structureMembership.members.filter(sm => (sm.memberType != member.memberType) || (sm.member != member.member));

            structureMembership.members.splice(memberIndex + 1, 0, member);

            this.table.renderRows();

            // Notify my parent of the change.
            this.onMembershipChange.emit(this.structureMemberships);
        }

        return;
    }

    public deleteMembership(structureMembership: MagicStructureMembership): void {
        let numMemberships: number = this.structureMemberships.length;

        let filteredMemberships: MagicStructureMembershipWithFormGroup[] =
            this.structureMemberships.filter(m => m != structureMembership);

        if (filteredMemberships.length != numMemberships) {
            this.structureMemberships = filteredMemberships;

            if (structureMembership.membershipSelected) {
                //this.setAddButtonToAddMember(false);
                this.setAddButtonBasedOnMemberships();
            }

            this.table.renderRows();

            // Notify my parent of the change.
            this.onMembershipChange.emit(this.structureMemberships);
        }

        return;
    }
    public deleteMember(structureMembership: MagicStructureMembership, member: MagicStructureMember, memberIndex: number): void {
        let numMembers: number = structureMembership.members.length;

        let filteredMembers: MagicStructureMember[] =
            structureMembership.members.filter(sm => (sm.memberType != member.memberType) || (sm.member != member.member));

        if (filteredMembers.length != numMembers) {
            structureMembership.members = filteredMembers;

            this.table.renderRows();

            // Notify my parent of the change.
            this.onMembershipChange.emit(this.structureMemberships);
        }

        return;
    }

    public importMemberships(): void {
        // Process an open file dialogue.
        this.importMembershipsFile.nativeElement.click();
    }

    // Helper methods.
    private get structureTypeName(): string {
        return (this.folderOrFormInstance.typeName() == Folder.TypeName ? 'Folder' : 'Form');
    }

    private deselectMembershipsExcept(membershipException: MagicStructureMembership = null): void {
        for (let iMembership: number = 0; iMembership < this.structureMemberships.length; iMembership++) {
            let membership: MagicStructureMembership = this.structureMemberships[iMembership];

            if ((membershipException == null) || (membershipException != membership)) {
                if (membership.membershipSelected) {
                    membership.membershipSelected = false;
                }
            }
        }
    }

    private setAddButtonBasedOnMemberships(): void {
        let bMembershipSelected: boolean = false;

        for (let iMembership: number = 0; iMembership < this.structureMemberships.length; iMembership++) {
            let membership: MagicStructureMembership = this.structureMemberships[iMembership];

            if (membership.membershipSelected) {
                bMembershipSelected = true;

                break;
            }
        }

        this.setMembershipSelected(bMembershipSelected);

        return;
    }

    private setMembershipSelected(bSelectedFlag: boolean): void {
        this.addMemberButtonCanEnable = bSelectedFlag;
    }

}
